/**
 * parseApiError — PT-43.1
 *
 * Extract the structured payload from an axios/fetch/plain-Error rejection.
 * PT-42 introduced a 409 shape:
 *
 *   { error: "document_locked", reason: "...", locked_fields: [...] }
 *
 * ...and the existing frontend catch blocks were all doing `catch { setError(...) }`
 * — swallowing the response body. This helper is the small piece of glue that
 * lets any handler ask "is this the amendment-exit case?" without having to
 * remember whether the caller was axios (`err.response.data`), a rethrown
 * plain object (`err.data`), or a bare Error (`err.message`).
 *
 * @param {unknown} err The value passed to a catch block.
 * @returns {{ code: string|null, reason: string|null, lockedFields: string[], status: number|null, raw: unknown }}
 *   - `code`         — the machine-readable error tag, e.g. `document_locked`.
 *   - `reason`       — human-readable "why".
 *   - `lockedFields` — array of field names the backend refused (may be []).
 *   - `status`       — HTTP status when known, else null.
 *   - `raw`          — the original error for downstream logging.
 */
export function parseApiError(err) {
  const out = { code: null, reason: null, lockedFields: [], status: null, raw: err };

  if (err == null) return out;

  // Axios wraps the server payload at err.response.data. Some callers rethrow
  // an object shaped like { data: {...}, status } directly. Bare Errors have
  // only .message and (sometimes) .status.
  const payload =
    (err && typeof err === 'object' && err.response && err.response.data) ||
    (err && typeof err === 'object' && err.data) ||
    null;

  out.status =
    (err && typeof err === 'object' && err.response && typeof err.response.status === 'number' && err.response.status) ||
    (err && typeof err === 'object' && typeof err.status === 'number' && err.status) ||
    null;

  if (payload && typeof payload === 'object') {
    // The backend's canonical error shape is `{ error: "...", reason: "...", locked_fields: [...] }`.
    // Some paths return `{ code: "..." }` instead; accept both.
    out.code = payload.error || payload.code || null;
    out.reason = payload.reason || payload.message || null;
    if (Array.isArray(payload.locked_fields)) {
      out.lockedFields = payload.locked_fields.slice();
    } else if (Array.isArray(payload.lockedFields)) {
      out.lockedFields = payload.lockedFields.slice();
    }
    return out;
  }

  // No structured payload — fall back to the Error's own message. This gives
  // callers *something* to display even when the network layer swallowed the
  // body (403s from CORS, disconnects, etc.).
  if (err && typeof err === 'object' && typeof err.message === 'string') {
    out.reason = err.message;
  } else if (typeof err === 'string') {
    out.reason = err;
  }

  return out;
}

/**
 * isDocumentLocked — PT-43.1 convenience
 *
 * Returns true iff the parsed error matches the PT-42 "document_locked" 409
 * shape. Callers use this to gate the "Create Change Order" affordance.
 */
export function isDocumentLocked(err) {
  const parsed = err && err.code !== undefined ? err : parseApiError(err);
  return parsed.code === 'document_locked';
}

export default parseApiError;
